import { createContext, useContext, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { PaginatedReport } from '../../shared/report-pagination.ts';
import { useSession } from '../state/session.tsx';

export function useReportPaging(filters: unknown) {
  const { company, currentLocation, user } = useSession();
  const key = JSON.stringify([company?.id, user?.id, currentLocation?.id, filters]);
  const [state, setState] = useState<{ key: string; pages: Record<string, number> }>({ key, pages: {} });
  const pages = state.key === key ? state.pages : {};
  if (state.key !== key) setState({ key, pages: {} });
  return {
    identity: JSON.stringify([company?.id, user?.id, currentLocation?.id]),
    pages: JSON.stringify(pages),
    setPage(collection: string, page: number) {
      setState({ key, pages: { ...pages, [collection]: page } });
    },
  };
}

type Paging = ReturnType<typeof useReportPaging>;
const Context = createContext<{ paging: Paging; metadata: PaginatedReport['pagination']; busy: boolean } | null>(null);
export function ReportPagingBoundary({
  paging,
  report,
  busy,
  children,
}: {
  paging?: Paging;
  report: PaginatedReport;
  busy: boolean;
  children: ReactNode;
}) {
  return (
    <Context.Provider value={paging ? { paging, metadata: report.pagination, busy } : null}>
      {children}
    </Context.Provider>
  );
}

export function ReportPager({ collection }: { collection?: string }) {
  const { t } = useTranslation();
  const value = useContext(Context);
  const page = collection ? value?.metadata?.[collection] : undefined;
  if (!page || !value || !collection) return null;
  return (
    <nav
      className="mt-4 flex flex-wrap items-center justify-between gap-3"
      aria-label={t('reports.paging.label', { collection })}
    >
      <button
        type="button"
        className="btn btn-secondary"
        disabled={value.busy || page.page <= 1}
        onClick={() => value.paging.setPage(collection, page.page - 1)}
      >
        {t('reports.paging.previous')}
      </button>
      <span className="text-sm text-[color:var(--color-muted)]" aria-live="polite">
        {t('reports.paging.page', { page: page.page })}
        {page.rows === 0 ? ` · ${t('reports.paging.empty')}` : ''}
      </span>
      <button
        type="button"
        className="btn btn-secondary"
        disabled={value.busy || !page.hasMore}
        onClick={() => value.paging.setPage(collection, page.page + 1)}
      >
        {t('reports.paging.next')}
      </button>
    </nav>
  );
}
